import { React, useState } from 'react';

import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import AssignmentIndRoundedIcon from '@mui/icons-material/AssignmentIndRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

import { Alerta } from '../../components/alerta';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { fetchFlask } from '../../utils/fetchFlask';
import { maskCpfCnpj } from '../../utils/maskCpfCnpj';
import { isCnpjDocument } from '../../utils/stringFormat';
import '../css/contractCreation.css';

const GerarProcuracaoContestacao = () => {
    const [cc, setCC] = useState('');
    const [cpfCnpj, setCpfCnpj] = useState('');
    const [representativeCpf, setRepresentativeCpf] = useState('');
    const [representativeName, setRepresentativeName] = useState('');
    const [alert, setAlert] = useState({ message: '', status: '', open: false });
    const [isSubmit, setIsSubmit] = useState(false);
    const isCnpj = isCnpjDocument(cpfCnpj);

    function handleDocumentChange (value) {
        const document = maskCpfCnpj(value);
        setCpfCnpj(document);
        if (!isCnpjDocument(document)) {
            setRepresentativeCpf('');
            setRepresentativeName('');
        }
    }

    async function submitProcuracao (e) {
        e.preventDefault();
        setIsSubmit(true);
        const body = JSON.stringify({
            cc,
            cpfCnpj,
            ...(isCnpj && { representativeCpf, representativeName })
        });

        try {
            const response = await fetchFlask('gerarProcuracaoContestacao', body);
            setAlert({ message: response.message, status: response.status, open: true });
            if (response.status === 'success') {
                setCC('');
                setCpfCnpj('');
                setRepresentativeCpf('');
                setRepresentativeName('');
            }
        } finally {
            setIsSubmit(false);
        }
    }

    const completedFields = [cc, cpfCnpj, ...(isCnpj ? [representativeCpf, representativeName] : [])].filter(Boolean).length;
    const totalFields = isCnpj ? 4 : 2;

    return (
        <Box className='mainBox contract-creation-page'>
            <Alerta texto={alert.message} status={alert.status} open={alert.open} handleClose={() => setAlert({ ...alert, open: false })} />
            <PageHeader>Procuração de Contestação</PageHeader>
            <form method='POST' id='formGerarProcuracaoContestacao' onSubmit={submitProcuracao} className='contract-creation-layout'>
                <Box className='contract-creation-workspace'>
                    <Box className='contract-creation-intro'>
                        <Box>
                            <h2>Criação de procuração de contestação</h2>
                            <p>Informe a conta contrato e os dados do titular para gerar o documento.</p>
                        </Box>
                    </Box>
                    <FormPanel icon={<Inventory2RoundedIcon />} step='Etapa 1' title='Conta contrato'>
                        <InputStrip icon={<ArticleRoundedIcon />}>
                            <TextField required label='CC' value={cc} onChange={(e) => setCC(e.target.value)} />
                        </InputStrip>
                    </FormPanel>
                    <FormPanel icon={<PersonSearchRoundedIcon />} step='Etapa 2' title='Documento do titular'>
                        <InputStrip icon={<BadgeRoundedIcon />}>
                            <TextField required label='CPF/CNPJ' value={cpfCnpj} onChange={(e) => handleDocumentChange(e.target.value)} />
                        </InputStrip>
                    </FormPanel>
                    {isCnpj && (
                        <FormPanel icon={<AssignmentIndRoundedIcon />} step='Etapa 3' title='Representante legal'>
                            <InputStrip icon={<BadgeRoundedIcon />}>
                                <TextField required label='CPF do Representante' value={representativeCpf} onChange={(e) => setRepresentativeCpf(maskCpfCnpj(e.target.value))} />
                            </InputStrip>
                            <InputStrip icon={<AssignmentIndRoundedIcon />}>
                                <TextField required label='Nome do Representante' value={representativeName} onChange={(e) => setRepresentativeName(e.target.value)} />
                            </InputStrip>
                        </FormPanel>
                    )}
                </Box>
                <Box className='contract-creation-summary'>
                    <Box className='contract-creation-summary-header'>
                        <FactCheckRoundedIcon />
                        <Box><span>Resumo</span><strong>Revisão da procuração</strong></Box>
                    </Box>
                    <Box className='contract-creation-summary-list'>
                        <SummaryItem label='CC' value={cc} emptyText='Não informada' />
                        <SummaryItem label='CPF/CNPJ' value={cpfCnpj} />
                        {isCnpj && <SummaryItem label='CPF do representante' value={representativeCpf} />}
                        {isCnpj && <SummaryItem label='Nome do representante' value={representativeName} />}
                    </Box>
                    <Box className='contract-creation-attention'>
                        <RuleRoundedIcon /><p>Revise os dados antes de gerar a procuração de contestação.</p>
                    </Box>
                    <Box className='contract-creation-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box><span>Campos obrigatórios</span><strong>{completedFields}/{totalFields} preenchidos</strong></Box>
                    </Box>
                    <SubmitButton isSubmit={isSubmit} startIcon={<ArticleRoundedIcon />}>Gerar Procuração</SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

const FormPanel = ({ icon, step, title, children }) => (
    <Box className='contract-creation-panel'>
        <Box className='contract-creation-section-heading'>
            {icon}<Box><span>{step}</span><strong>{title}</strong></Box>
        </Box>
        {children}
    </Box>
);

const InputStrip = ({ icon, children }) => (
    <Box className='contract-creation-input-strip'>{icon}{children}</Box>
);

const SummaryItem = ({ label, value, emptyText = 'Não informado' }) => (
    <Box><span>{label}</span><strong className={!value ? 'is-empty' : undefined}>{value || emptyText}</strong></Box>
);

export default GerarProcuracaoContestacao;
